/**
 * LIG — Make It Yours (PDP customization suite).
 *
 * The modal composites mockups IN THE BROWSER from the middleware's public
 * geometry payload: color underlay + Printful ghost blank + the product's own
 * print files, positioned by fraction-of-print-area boxes. Placement is
 * locked (operator-approved numbers) — customers pick cut, print position,
 * color, size; never coordinates.
 *
 * Known versions (twin registry) add to cart instantly. A first-ever combo is
 * materialized on demand by the middleware (~2 min); the button becomes a
 * progress bar and the piece drops into the cart when ready. Cart pipeline is
 * the proven family-builder one (CartAddEvent + explicit drawer open).
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const POLL_MS = 5000;
const BUILD_TIMEOUT_MS = 5 * 60 * 1000;
const PROGRESS_LINES = [
  'Cutting your print file…',
  'Inking the mockups — every color gets its own…',
  'Quality pass on the placement…',
  'Publishing your version…',
  'Almost — dotting the last i…',
];
const FIT_NOTES = {
  tee: 'Modern retail fit. Runs a touch trim — size up for a relaxed look.',
  comfort_colors_tee: 'Heavyweight 6.1 oz cotton with a traditional boxier fit. True to size.',
  women_vneck: "Women's relaxed cut with a slightly longer body. True to size.",
};

class LigCustomize extends HTMLElement {
  #cfg = null;
  #data = null;
  #modal = null;
  #state = { cut: 'tee', position: 'back', color: null, size: null, view: 'back' };
  #twinCache = new Map(); // comboKey -> {handle, variants[]}
  #building = false;
  #pollTimer = null;
  #progressTimer = null;
  #saveHandle = null; // twin handle of the piece just added — drives the save-for-later card

  /** Modal-first lookup: the overlay is PORTALED to <body> (Horizon block
   *  wrappers carry transforms that break position:fixed descendants). */
  #q(sel) { return this.#modal?.querySelector(sel) || this.querySelector(sel); }
  #qa(sel) {
    const inModal = this.#modal ? [...this.#modal.querySelectorAll(sel)] : [];
    return inModal.length ? inModal : [...this.querySelectorAll(sel)];
  }

  connectedCallback() {
    try {
      this.#cfg = JSON.parse(this.querySelector('[data-cz-config]')?.textContent || '{}');
    } catch { this.#cfg = {}; }
    this.#modal = this.#q('[data-cz-modal]');
    if (this.#modal) document.body.appendChild(this.#modal); // escape transformed ancestors
    this.querySelector('[data-cz-open]')?.addEventListener('click', () => this.#open());
    this.#q('[data-cz-close]')?.addEventListener('click', () => this.#close());
    this.#modal?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.#close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#modal && !this.#modal.hidden) this.#close();
    });
    this.#qa('[data-cz-view]').forEach((b) =>
      b.addEventListener('click', () => { this.#state.view = b.dataset.czView; this.#paintStage(); this.#paintViews(); })
    );
    this.#qa('[data-cz-position]').forEach((b) =>
      b.addEventListener('click', () => this.#setPosition(b.dataset.czPosition))
    );
    this.#q('[data-cz-cta]')?.addEventListener('click', () => this.#submit());
  }

  disconnectedCallback() {
    this.#modal?.remove();
  }

  async #open() {
    const modal = this.#modal;
    if (!modal) return;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.querySelector('[data-cz-close]')?.focus();
    if (this.#data) return;
    try {
      const r = await fetch(`${this.#cfg.apiBase}/api/pdp-customize-data?product=${encodeURIComponent(this.#cfg.handle)}`);
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || 'could not load');
      this.#data = d;
      // starting color: the color they were viewing on the PDP
      const src = this.#cut();
      this.#state.color = src.palette.some((p) => p.name === this.#cfg.currentColor)
        ? this.#cfg.currentColor
        : src.palette[0]?.name;
      this.#q('[data-cz-loading]')?.remove();
      this.#q('[data-cz-views]').hidden = false;
      this.#renderAll();
      this.#preloadBlanks();
    } catch (err) {
      const load = this.#q('[data-cz-loading]');
      if (load) load.innerHTML = 'The fitting room didn’t open — refresh and try again.';
    }
  }

  #close() {
    if (this.#modal) this.#modal.hidden = true;
    document.documentElement.style.overflow = '';
    this.#stopPolling();
  }

  #cut(key) { return this.#data.cuts.find((c) => c.key === (key || this.#state.cut)); }
  #comboKey() {
    if (this.#state.cut !== 'tee') return `g:${this.#state.cut}`;
    if (this.#state.position === 'front') return 'front';
    return null; // the product itself
  }

  /* ---------- rendering ---------- */

  #renderAll() {
    this.#renderCuts();
    this.#renderSwatches();
    this.#renderSizes();
    this.#paintPosition();
    this.#paintViews();
    this.#paintStage();
    this.#paintFooter();
  }

  #renderCuts() {
    const host = this.#q('[data-cz-cuts]');
    host.replaceChildren(...this.#data.cuts.map((c) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lig-cz__cut' + (c.key === this.#state.cut ? ' is-on' : '');
      b.innerHTML = `<span class="lig-cz__cutname"></span><span class="lig-cz__cutprice">from $${c.priceFrom}</span><span class="lig-cz__cutblurb"></span>`;
      b.querySelector('.lig-cz__cutname').textContent = c.label + (c.isSource ? ' · as shown' : '');
      b.querySelector('.lig-cz__cutblurb').textContent = c.blurb;
      b.addEventListener('click', () => this.#setCut(c.key));
      return b;
    }));
  }

  #renderSwatches() {
    const cut = this.#cut();
    const host = this.#q('[data-cz-swatches]');
    host.replaceChildren(...cut.palette.filter((p) => cut.colors[p.name]).map((p) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lig-cz__swatch' + (p.name === this.#state.color ? ' is-on' : '');
      b.style.background = p.hex;
      b.title = p.name;
      b.setAttribute('aria-label', p.name);
      b.addEventListener('click', () => { this.#state.color = p.name; this.#renderSwatches(); this.#paintStage(); });
      return b;
    }));
    this.#q('[data-cz-colorname]').textContent = this.#state.color || '';
  }

  #renderSizes() {
    const cut = this.#cut();
    const host = this.#q('[data-cz-sizes]');
    if (this.#state.size && !cut.sizes.includes(this.#state.size)) this.#state.size = null;
    host.replaceChildren(...cut.sizes.map((s) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lig-cz__size' + (s === this.#state.size ? ' is-on' : '');
      b.textContent = s;
      b.addEventListener('click', () => { this.#state.size = s; this.#renderSizes(); this.#paintFooter(); });
      return b;
    }));
    this.#q('[data-cz-fit]').textContent = FIT_NOTES[cut.key] || '';
  }

  #paintPosition() {
    const isSource = this.#state.cut === 'tee';
    this.#qa('[data-cz-position]').forEach((b) => {
      b.classList.toggle('is-on', b.dataset.czPosition === this.#state.position);
      b.disabled = b.dataset.czPosition === 'front' && !isSource;
    });
    this.#q('[data-cz-posnote]').hidden = isSource;
  }

  #paintViews() {
    this.#qa('[data-cz-view]').forEach((b) =>
      b.classList.toggle('is-on', b.dataset.czView === this.#state.view)
    );
  }

  /** The heart: composite color underlay + ghost blank + design at the
   *  fraction box, scaled from template space to stage pixels. */
  #paintStage() {
    const stage = this.#q('[data-cz-stage]');
    const cut = this.#cut();
    const colorEntry = cut.colors[this.#state.color];
    if (!colorEntry) { stage.replaceChildren(); return; }
    const view = colorEntry[this.#state.view];
    if (!view) { stage.replaceChildren(); return; }

    const frag = document.createDocumentFragment();
    const bg = document.createElement('div');
    bg.className = 'lig-cz__bgfill';
    bg.style.background = view.bg || cut.palette.find((p) => p.name === this.#state.color)?.hex || '#333';
    frag.appendChild(bg);

    const blank = document.createElement('img');
    blank.className = 'lig-cz__blank';
    blank.src = view.blank;
    blank.alt = '';
    frag.appendChild(blank);

    // which box shows on this view for the current position mode
    let frac = null;
    let designUrl = null;
    let artAspect = this.#data.designs.back.aspect || 1;
    if (this.#state.position === 'front') {
      if (this.#state.view === 'front') { frac = cut.boxes.frontBig; designUrl = this.#data.designs.back.url; }
    } else {
      if (this.#state.view === 'back') { frac = cut.boxes.back; designUrl = this.#data.designs.back.url; }
      else if (cut.boxes.chest && this.#data.designs.chest) {
        frac = cut.boxes.chest;
        designUrl = this.#data.designs.chest.url;
        artAspect = this.#data.designs.chest.aspect || artAspect;
      }
    }
    if (frac && designUrl) {
      const d = document.createElement('img');
      d.className = 'lig-cz__design';
      d.src = designUrl;
      d.alt = '';
      // UNIFORM width-anchored mapping (the print pipeline's rule) + the art's
      // own aspect for height — the template photo's print-area rect has a
      // different pixel aspect than the physical area and must never squish art.
      const ratio = (cut.areaRatio && (this.#state.view === 'back' ? cut.areaRatio.back : cut.areaRatio.front)) || view.pa.h / view.pa.w;
      const wPx = frac.w * view.pa.w;                    // template px
      const hPx = wPx / artAspect;
      const leftPx = view.pa.x + frac.x * view.pa.w;
      const cyPx = view.pa.y + (frac.y + frac.h / 2) * view.pa.w * ratio;
      d.style.left = `${(leftPx / view.tw) * 100}%`;
      d.style.top = `${((cyPx - hPx / 2) / view.th) * 100}%`;
      d.style.width = `${(wPx / view.tw) * 100}%`;
      d.style.height = `${(hPx / view.th) * 100}%`;
      frag.appendChild(d);
    }
    stage.replaceChildren(frag);
  }

  #paintFooter() {
    const cut = this.#cut();
    const priceEl = this.#q('[data-cz-price]');
    const cta = this.#q('[data-cz-cta]');
    if (this.#building) return; // progress owns the footer
    if (this.#state.size) {
      priceEl.textContent = `$${cut.pricing[this.#state.size] || cut.priceFrom}`;
      cta.disabled = false;
      const combo = this.#comboKey();
      cta.textContent = combo ? 'Make mine' : 'Add to cart';
    } else {
      priceEl.innerHTML = `$${cut.priceFrom}<small>pick a size</small>`;
      cta.disabled = true;
      cta.textContent = 'Pick a size';
    }
    cta.classList.remove('is-success');
  }

  #status(text, isErr) {
    const el = this.#q('[data-cz-status]');
    el.textContent = text || '';
    el.classList.toggle('is-err', !!isErr);
  }

  /* ---------- state changes ---------- */

  #setCut(key) {
    if (this.#building) return;
    const prevPalette = this.#cut().palette;
    this.#state.cut = key;
    const cut = this.#cut(key);
    if (key !== 'tee' && this.#state.position === 'front') this.#state.position = 'back';
    // map the color across palettes: prefer the target color that lists the
    // current one in suggestFor; else keep same name; else first offered
    const current = this.#state.color;
    let next = null;
    for (const [name, entry] of Object.entries(cut.colors)) {
      if (entry.suggestFor && entry.suggestFor.includes(current)) { next = name; break; }
    }
    if (!next && cut.colors[current]) next = current;
    if (!next) next = cut.palette.find((p) => cut.colors[p.name])?.name;
    this.#state.color = next;
    void prevPalette;
    this.#renderAll();
    this.#status('');
  }

  #setPosition(pos) {
    if (this.#building) return;
    if (pos === 'front' && this.#state.cut !== 'tee') return;
    this.#state.position = pos;
    this.#state.view = pos === 'front' ? 'front' : 'back';
    this.#paintPosition();
    this.#paintViews();
    this.#paintStage();
    this.#paintFooter();
    this.#status('');
  }

  /* ---------- submit / build / cart ---------- */

  async #submit() {
    if (!this.#state.size || this.#building) return;
    const combo = this.#comboKey();
    this.#status('');
    if (!combo) {
      // the product on this page, as-is — not a custom piece, no save card
      this.#saveHandle = null;
      const vid = this.#cfg.sourceVariants[`${this.#state.color}|${this.#state.size}`];
      if (!vid) return this.#status('That color/size combination isn’t available.', true);
      return this.#addToCart(vid);
    }
    const cached = this.#twinCache.get(combo);
    if (cached) return this.#addTwinVariant(cached);
    this.#startBuild(combo);
  }

  #buildBody() {
    const body = { product: this.#cfg.handle };
    if (this.#state.cut !== 'tee') body.cut = this.#state.cut;
    else body.position = 'front';
    return body;
  }

  async #startBuild(combo) {
    this.#building = true;
    const knownTwin = !!(this.#data.twins && this.#data.twins[combo]);
    const cta = this.#q('[data-cz-cta]');
    const progress = this.#q('[data-cz-progress]');
    const ptext = this.#q('[data-cz-progresstext]');
    cta.hidden = true;
    progress.hidden = false;
    ptext.textContent = knownTwin ? 'Fetching your version…' : 'Stitching your version — about two minutes, and only ever the first time.';
    let lineIdx = 0;
    if (!knownTwin) {
      this.#progressTimer = setInterval(() => {
        ptext.textContent = PROGRESS_LINES[lineIdx++ % PROGRESS_LINES.length];
      }, 14000);
    }

    const deadline = Date.now() + BUILD_TIMEOUT_MS;
    const poll = async () => {
      try {
        const r = await fetch(`${this.#cfg.apiBase}/api/pdp-customize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.#buildBody()),
        });
        const d = await r.json();
        if (d.state === 'ready' && d.twin) {
          this.#twinCache.set(combo, d.twin);
          this.#endBuild();
          return this.#addTwinVariant(d.twin);
        }
        if (d.state === 'failed' || (!r.ok && r.status !== 429)) {
          this.#endBuild();
          return this.#status(d.error || 'That build hit a snag — try again in a minute.', true);
        }
        if (Date.now() > deadline) {
          this.#endBuild();
          return this.#status('Taking longer than it should — leave this open or try again shortly.', true);
        }
        this.#pollTimer = setTimeout(poll, POLL_MS);
      } catch {
        if (Date.now() > deadline) {
          this.#endBuild();
          return this.#status('Connection hiccup — try again.', true);
        }
        this.#pollTimer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
  }

  #endBuild() {
    this.#building = false;
    this.#stopPolling();
    this.#q('[data-cz-cta]').hidden = false;
    this.#q('[data-cz-progress]').hidden = true;
    this.#paintFooter();
  }

  #stopPolling() {
    clearTimeout(this.#pollTimer);
    clearInterval(this.#progressTimer);
    this.#pollTimer = null;
    this.#progressTimer = null;
  }

  #addTwinVariant(twin) {
    const match = (twin.variants || []).find(
      (v) => v.color === this.#state.color && v.size === this.#state.size && v.available !== false
    );
    if (!match) return this.#status('That exact combo didn’t come out — try another color or size.', true);
    this.#saveHandle = twin.handle || null;
    return this.#addToCart(match.id);
  }

  async #addToCart(variantId) {
    const cta = this.#q('[data-cz-cta]');
    cta.disabled = true;
    cta.textContent = 'Adding…';
    try {
      const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((n) => n.dataset.sectionId)
        .filter(Boolean);
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        document.dispatchEvent(new CartErrorEvent('lig-customize', parsed.message, parsed.description, parsed.errors));
        this.#status(parsed.description || 'Couldn’t add to cart — try again.', true);
        this.#paintFooter();
        return;
      }
      let cart = null;
      try { cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json(); } catch { /* optional */ }
      document.dispatchEvent(new CartAddEvent(cart ?? parsed, 'lig-customize', {
        source: 'lig-customize',
        itemCount: cart?.item_count,
        sections: parsed.sections,
      }));
      cta.textContent = 'In your cart ✓';
      cta.classList.add('is-success');
      this.#status('');
      // Let the drawer take the stage: close the modal, then open the cart.
      setTimeout(() => {
        this.#close();
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') { try { drawer.open(); } catch { /* no-op */ } }
        this.#paintFooter(); // reset for the next visit
        this.#maybeSpawnSaveCard(); // custom pieces: offer to email the link for later
      }, 900);
    } catch {
      this.#status('Couldn’t add to cart — try again.', true);
      this.#paintFooter();
    }
  }

  /* ---------- save for later ---------- */

  /** After a CUSTOM piece lands in the cart: a small floating card offers to
   *  email the customer their design's link (the twin is hidden — this link
   *  is the only way back). Non-blocking: the cart drawer opens as usual. */
  #maybeSpawnSaveCard() {
    const handle = this.#saveHandle;
    this.#saveHandle = null;
    if (!handle) return;
    try { if (sessionStorage.getItem('lig-czsave-hush')) return; } catch { /* private mode */ }
    document.querySelector('.lig-czsave')?.remove();

    const card = document.createElement('div');
    card.className = 'lig-czsave';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-label', 'Save your custom piece');
    card.innerHTML = `
      <button type="button" class="lig-czsave__x" aria-label="Dismiss">&times;</button>
      <div class="lig-czsave__h">Your custom piece is one of one.</div>
      <p class="lig-czsave__p">It's in your cart — want the link too? We'll email your design so you can come back for it anytime.</p>
      <form class="lig-czsave__row" novalidate>
        <input class="lig-czsave__in" type="email" inputmode="email" autocomplete="email" placeholder="you@email.com" required />
        <button class="lig-czsave__go" type="submit">Email it</button>
      </form>
      <div class="lig-czsave__note">One email with your link — no list, no spam.</div>`;
    document.body.appendChild(card);

    const hush = () => { try { sessionStorage.setItem('lig-czsave-hush', '1'); } catch { /* ok */ } };
    const dismiss = () => { card.classList.add('is-out'); setTimeout(() => card.remove(), 350); };
    let idleTimer = setTimeout(dismiss, 60000);
    card.querySelector('.lig-czsave__x').addEventListener('click', () => { hush(); dismiss(); });

    const form = card.querySelector('form');
    const input = card.querySelector('.lig-czsave__in');
    const go = card.querySelector('.lig-czsave__go');
    input.addEventListener('input', () => { clearTimeout(idleTimer); idleTimer = setTimeout(dismiss, 180000); });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = (input.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { input.classList.add('is-err'); input.focus(); return; }
      input.classList.remove('is-err');
      go.disabled = true; go.textContent = 'Sending…';
      try {
        const r = await fetch(`${this.#cfg.apiBase}/api/customize-save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, handle }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'try again in a minute');
        hush();
        card.querySelector('.lig-czsave__h').textContent = 'Sent — check your inbox.';
        card.querySelector('.lig-czsave__p').textContent = 'Your design and its link are on the way.';
        form.remove();
        card.querySelector('.lig-czsave__note').remove();
        clearTimeout(idleTimer);
        setTimeout(dismiss, 4500);
      } catch (err) {
        go.disabled = false; go.textContent = 'Email it';
        card.querySelector('.lig-czsave__note').textContent = (err && err.message) || 'Couldn’t send — try again.';
        card.querySelector('.lig-czsave__note').classList.add('is-err');
      }
    });
  }

  /* ---------- niceties ---------- */

  /** Warm the blank images for the current cut so color/view flips are instant. */
  #preloadBlanks() {
    const cut = this.#cut();
    for (const entry of Object.values(cut.colors)) {
      for (const v of ['front', 'back']) {
        if (entry[v]?.blank) { const i = new Image(); i.src = entry[v].blank; }
      }
    }
  }
}

customElements.define('lig-customize', LigCustomize);
